/**
 * Sub-components and helpers extracted from SiteModeGate so the gate file
 * itself only handles the bypass logic and stays a small orchestrator.
 *
 * The HTML body is rendered via `safeHtml()` which routes through DOMPurify
 * (see /app/frontend/src/lib/sanitize.js) — never use raw `dangerouslySetInnerHTML`
 * elsewhere in this module.
 */
import { Link } from "react-router-dom";
import { Compass, Wrench, Hourglass, Sparkles } from "lucide-react";
import { safeHtml } from "@/lib/sanitize";

export const DEFAULT_SPLASH = {
  coming_soon: {
    icon: Hourglass,
    eyebrow: "Coming soon",
    title: "Something quietly useful is on the way.",
    body_html:
      "<p>We're putting the finishing touches on a calm, practical platform for navigating AI-driven career change. Leave your email on the contact page and we'll let you know the moment we open the doors.</p>",
    button_label: "Contact us",
    button_url: "/contact",
    tone: "emerald",
    image_url: "/brand-logo.jpg",
  },
  maintenance: {
    icon: Wrench,
    eyebrow: "Maintenance",
    title: "We're making things better. Back shortly.",
    body_html:
      "<p>A short scheduled maintenance window is in progress. Your data is safe — please try again in a few minutes.</p>",
    button_label: "Contact us",
    button_url: "/contact",
    tone: "amber",
    image_url: "/brand-logo.jpg",
  },
  custom: {
    icon: Sparkles,
    eyebrow: "",
    title: "Your custom cover page",
    body_html: "<p>Edit this in the admin console under Cover pages.</p>",
    button_label: "Contact us",
    button_url: "/contact",
    tone: "slate",
  },
};

export const ALWAYS_ALLOWED = new Set(["/login", "/forgot-password", "/contact"]);

export const TONE_CLS = {
  emerald: "bg-emerald-100 text-emerald-700",
  amber: "bg-amber-100 text-amber-700",
  slate: "bg-slate-100 text-slate-700",
  sky: "bg-sky-100 text-sky-700",
  rose: "bg-rose-100 text-rose-700",
};

function getBackendOrigin() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
}

export function resolveImageUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${getBackendOrigin()}${url}`;
  return url;
}

export function isAllowedDuringLockdown(path) {
  return (
    ALWAYS_ALLOWED.has(path) ||
    path.startsWith("/admin") ||
    path.startsWith("/auth/")
  );
}

function CoverHero({ imageUrl, Icon, toneCls }) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        className="mx-auto mb-5 max-h-40 rounded-2xl object-contain"
        data-testid="site-mode-image"
      />
    );
  }
  return (
    <span
      className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${toneCls}`}
    >
      <Icon className="h-5 w-5" />
    </span>
  );
}

function CoverBody({ message, html }) {
  if (message) {
    return (
      <p className="mt-5 text-slate-600 leading-relaxed">{message}</p>
    );
  }
  return (
    <div
      className="mt-5 text-slate-600 leading-relaxed prose prose-sm mx-auto [&_a]:text-sky-700 [&_a]:underline"
      // safeHtml routes through DOMPurify — scanner false-positive flagging
      // intentional, this is the project's documented sanitiser.
      dangerouslySetInnerHTML={safeHtml(html)}
      data-testid="site-mode-body"
    />
  );
}

function CoverActions() {
  // "Contact us" button intentionally removed per product decision (Feb 2026).
  // The admin CoverEditor still exposes button_label / button_url fields and
  // they are persisted, but they are not rendered here. To restore, re-add a
  // <Link> using `cover.button_label` / `cover.button_url` (see git history).
  return (
    <div className="mt-8 flex items-center justify-center gap-3">
      <Link
        to="/login"
        className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        data-testid="site-mode-login-link"
      >
        <span className="inline-flex items-center gap-2">
          <Compass className="h-3.5 w-3.5" /> Admin login
        </span>
      </Link>
    </div>
  );
}

export function CoverSplash({ modeKey, cover, message }) {
  const defaults = DEFAULT_SPLASH[modeKey] || DEFAULT_SPLASH.coming_soon;
  const Icon = defaults.icon || Sparkles;
  const toneCls = TONE_CLS[cover.tone] || TONE_CLS.slate;
  const imageUrl = resolveImageUrl(cover.image_url);
  const body = cover.body_html || defaults.body_html;

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
      data-testid={`site-mode-${modeKey}`}
    >
      <div className="max-w-xl text-center">
        <CoverHero imageUrl={imageUrl} Icon={Icon} toneCls={toneCls} />
        {cover.eyebrow ? (
          <p className="mt-4 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
            {cover.eyebrow}
          </p>
        ) : null}
        <h1
          className="mt-2 text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight"
          data-testid="site-mode-title"
        >
          {cover.title || defaults.title}
        </h1>
        <CoverBody message={message} html={body} />
        <CoverActions />
      </div>
    </div>
  );
}
